import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import {
  PiEnvelope, PiPlus, PiTrash, PiCrown, PiGoogleLogo,
  PiSignOut, PiPuzzlePiece, PiCheckCircle, PiArrowSquareOut,
  PiShieldCheck, PiSpinner, PiWarning, PiBuildings, PiX,
} from 'react-icons/pi';
import '../styles/RepDashboard.css';

const FUNCTIONS_BASE = process.env.REACT_APP_FUNCTIONS_BASE || 'https://us-central1-up-convert.cloudfunctions.net';

function RepDashboard() {
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState('');
  const [toast, setToast] = useState(null);

  // Client setup modal state
  const [setupModal, setSetupModal] = useState(null); // { email } | null
  const [orgClients, setOrgClients] = useState([]);
  const [orgClientsLoading, setOrgClientsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // ── Auth ───────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) { navigate('/login', { replace: true }); return; }
      setCurrentUser(user);
    });
    return () => unsub();
  }, [navigate]);

  // ── Fetch connected accounts ──────────────────────────────────────────────────
  const fetchAccounts = useCallback(async () => {
    if (!currentUser) return [];
    setLoadingAccounts(true);
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${FUNCTIONS_BASE}/listConnectedAccounts`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        const fetched = data.accounts || [];
        setAccounts(fetched);
        return fetched;
      }
    } catch (_) {
      showToast('Failed to load connected accounts', 'error');
    } finally {
      setLoadingAccounts(false);
    }
    return [];
  }, [currentUser, showToast]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  // ── Fetch org clients (for picker) ────────────────────────────────────────────
  const fetchOrgClients = useCallback(async () => {
    if (!currentUser) return;
    setOrgClientsLoading(true);
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${FUNCTIONS_BASE}/listOrgClients`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOrgClients(data.clients || []);
      }
    } catch (_) {} finally {
      setOrgClientsLoading(false);
    }
  }, [currentUser]);

  // ── Handle ?connected=true / ?error callbacks ─────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected') === 'true';
    const error = params.get('error');

    if (connected || error) {
      // Clean URL immediately
      params.delete('connected');
      params.delete('error');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }

    if (error) { showToast(error, 'error'); return; }

    if (connected) {
      showToast('Client email connected!');
      // Re-fetch accounts and auto-open setup modal if the new account needs it
      fetchAccounts().then((fetched) => {
        const needsSetup = fetched.find((a) => a.needsClientSetup);
        if (needsSetup) openSetupModal(needsSetup.email);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Client setup modal ────────────────────────────────────────────────────────
  const openSetupModal = (email) => {
    setSetupModal({ email });
    setClientSearch('');
    setNewClientName('');
    fetchOrgClients();
  };

  const closeSetupModal = () => {
    setSetupModal(null);
    setClientSearch('');
    setNewClientName('');
  };

  const handleAssignClient = async (accountEmail, clientId, clientName) => {
    if (!currentUser) return;
    setSetupLoading(true);
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${FUNCTIONS_BASE}/setAccountClient`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ accountEmail, clientId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to assign client');

      // Update local state
      setAccounts((prev) => prev.map((a) =>
        a.email === accountEmail
          ? { ...a, clientId, clientName: data.clientName || clientName, needsClientSetup: false }
          : a
      ));
      closeSetupModal();
      showToast(`Linked to ${data.clientName || clientName}`);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleCreateAndAssign = async () => {
    if (!currentUser || !setupModal || !newClientName.trim()) return;
    setSetupLoading(true);
    try {
      const idToken = await currentUser.getIdToken();

      // Create the client
      const createRes = await fetch(`${FUNCTIONS_BASE}/createOrgClient`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ name: newClientName.trim() }),
      });
      const createData = await createRes.json();
      if (!createRes.ok) throw new Error(createData.error || 'Failed to create client');

      // Then assign
      await handleAssignClient(setupModal.email, createData.clientId, createData.name);
      setOrgClients((prev) => [...prev, { clientId: createData.clientId, name: createData.name }]);
    } catch (err) {
      showToast(err.message, 'error');
      setSetupLoading(false);
    }
  };

  // ── Connect new client email ──────────────────────────────────────────────────
  const handleConnectEmail = async () => {
    if (!currentUser) return;
    setConnectLoading(true);
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${FUNCTIONS_BASE}/initClientEmailAuth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ provider: 'google' }),
      });
      const data = await res.json();
      if (data.redirectUrl) { window.location.href = data.redirectUrl; return; }
      throw new Error(data.error || 'Failed to start auth flow');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setConnectLoading(false);
    }
  };

  // ── Disconnect ────────────────────────────────────────────────────────────────
  const handleDisconnect = async (email) => {
    if (!window.confirm(`Disconnect ${email}? You'll lose calendar and email access for this account.`)) return;
    setDisconnectLoading(email);
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(`${FUNCTIONS_BASE}/disconnectClientEmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) { setAccounts((prev) => prev.filter((a) => a.email !== email)); showToast(`${email} disconnected`); }
      else showToast(data.error || 'Failed to disconnect', 'error');
    } catch (_) { showToast('Network error', 'error'); }
    finally { setDisconnectLoading(''); }
  };

  const handleSignOut = async () => { await signOut(auth); navigate('/login', { replace: true }); };

  const filteredClients = orgClients.filter((c) =>
    !clientSearch || c.name.toLowerCase().includes(clientSearch.toLowerCase())
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="rep-shell">
      {/* Sidebar */}
      <aside className="rep-sidebar">
        <Link to="/" className="rep-brand">
          <span className="brand-primary">Up</span>
          <span className="brand-accent">Convert</span>
        </Link>
        <p className="rep-label">Rep Dashboard</p>
        <nav className="rep-nav">
          <span className="rep-nav-item active"><PiEnvelope size={20} /><span>Connected Emails</span></span>
        </nav>
        <div style={{ marginTop: 'auto' }}>
          <button className="rep-signout" type="button" onClick={handleSignOut}>
            <PiSignOut size={18} /><span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="rep-main">
        <header className="rep-header">
          <p className="rep-eyebrow">Your Workspace</p>
          <h1 className="rep-title">Connected Client Emails</h1>
          <p className="rep-description">
            Connect the email accounts you use for each client. We'll access their calendar and send follow-up emails on your behalf.
          </p>
        </header>

        <div className="rep-content">
          <section className="rep-card accounts-card">
            <div className="accounts-header">
              <div>
                <h2 className="accounts-title">Your Accounts</h2>
                <p className="accounts-subtitle">{accounts.length} email{accounts.length !== 1 ? 's' : ''} connected</p>
              </div>
              <button className="rep-btn rep-btn-primary" onClick={handleConnectEmail} disabled={connectLoading}>
                {connectLoading ? <><PiSpinner size={18} className="spin" /> Connecting…</> : <><PiPlus size={18} /> Connect email</>}
              </button>
            </div>

            {loadingAccounts ? (
              <div className="accounts-loading"><div className="loading-graphic" aria-hidden="true" /><p>Loading accounts…</p></div>
            ) : accounts.length === 0 ? (
              <div className="accounts-empty">
                <PiEnvelope size={40} />
                <h3>No accounts connected yet</h3>
                <p>Connect your first client email to get started.</p>
                <button className="rep-btn rep-btn-primary" onClick={handleConnectEmail} disabled={connectLoading}>
                  <PiPlus size={18} /> Connect your first email
                </button>
              </div>
            ) : (
              <div className="accounts-list">
                {accounts.map((account) => (
                  <div key={account.email} className={`account-row ${account.isMaster ? 'account-master' : ''} ${account.needsClientSetup ? 'account-needs-setup' : ''}`}>
                    <div className="account-avatar">
                      {account.isMaster ? <PiCrown size={20} /> : <PiGoogleLogo size={20} />}
                    </div>

                    <div className="account-info">
                      <div className="account-email">
                        {account.email}
                        {account.isMaster && <span className="master-badge">Master</span>}
                        {account.clientName && !account.needsClientSetup && (
                          <span className="client-badge"><PiBuildings size={12} />{account.clientName}</span>
                        )}
                      </div>
                      <div className="account-meta">
                        {account.displayName && <span>{account.displayName}</span>}
                        {!account.needsClientSetup && (
                          <span className="account-scopes"><PiCheckCircle size={14} />Calendar + Email</span>
                        )}
                        <span className="account-provider">{account.provider}</span>
                      </div>
                      {account.needsClientSetup && (
                        <div className="setup-warning">
                          <PiWarning size={14} />
                          <span>Complete setup — assign this inbox to a client so meetings are tracked correctly.</span>
                          <button className="rep-btn rep-btn-warning rep-btn-sm" onClick={() => openSetupModal(account.email)}>
                            Complete setup
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="account-actions">
                      {account.isMaster ? (
                        <span className="account-protected" title="Master account cannot be disconnected"><PiShieldCheck size={18} /></span>
                      ) : (
                        <button
                          className="rep-btn rep-btn-ghost rep-btn-danger"
                          onClick={() => handleDisconnect(account.email)}
                          disabled={disconnectLoading === account.email}
                          title="Disconnect this account"
                        >
                          {disconnectLoading === account.email ? <PiSpinner size={18} className="spin" /> : <PiTrash size={18} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Chrome Extension Card */}
          <section className="rep-card extension-card">
            <div className="extension-icon"><PiPuzzlePiece size={32} /></div>
            <div className="extension-info">
              <h3>Chrome Extension</h3>
              <p>Install the UpConvert Chrome Extension to view meeting summaries and send follow-up emails right from your browser.</p>
            </div>
            <a href="https://chrome.google.com/webstore" target="_blank" rel="noopener noreferrer" className="rep-btn rep-btn-secondary">
              <PiArrowSquareOut size={18} /> Install Extension
            </a>
          </section>
        </div>
      </main>

      {/* Client setup modal */}
      {setupModal && (
        <div className="rep-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeSetupModal(); }}>
          <div className="rep-modal">
            <div className="rep-modal-header">
              <div>
                <h2 className="rep-modal-title">Complete account setup</h2>
                <p className="rep-modal-sub">Which client is <strong>{setupModal.email}</strong> calling on behalf of?</p>
              </div>
              <button className="rep-modal-close" onClick={closeSetupModal} disabled={setupLoading}><PiX size={20} /></button>
            </div>

            <div className="rep-modal-body">
              {orgClientsLoading ? (
                <div className="modal-loading"><PiSpinner size={24} className="spin" /><span>Loading clients…</span></div>
              ) : (
                <>
                  {orgClients.length > 0 && (
                    <div className="client-picker-section">
                      <p className="picker-label">Select an existing client</p>
                      <input
                        className="picker-search"
                        placeholder="Search clients…"
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        autoFocus
                      />
                      <div className="client-list">
                        {filteredClients.length === 0 ? (
                          <p className="picker-empty">No clients match "{clientSearch}"</p>
                        ) : (
                          filteredClients.map((c) => (
                            <button
                              key={c.clientId}
                              className="client-option"
                              onClick={() => handleAssignClient(setupModal.email, c.clientId, c.name)}
                              disabled={setupLoading}
                            >
                              <PiBuildings size={16} />
                              <span>{c.name}</span>
                              {setupLoading && <PiSpinner size={14} className="spin" style={{ marginLeft: 'auto' }} />}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  <div className={`client-create-section ${orgClients.length > 0 ? 'has-divider' : ''}`}>
                    <p className="picker-label">{orgClients.length > 0 ? 'Or create a new client' : 'Create a client'}</p>
                    <div className="client-create-row">
                      <input
                        className="picker-search"
                        placeholder="Client name, e.g. Acme Inc"
                        value={newClientName}
                        onChange={(e) => setNewClientName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && newClientName.trim()) handleCreateAndAssign(); }}
                        autoFocus={orgClients.length === 0}
                        disabled={setupLoading}
                      />
                      <button
                        className="rep-btn rep-btn-primary"
                        onClick={handleCreateAndAssign}
                        disabled={!newClientName.trim() || setupLoading}
                      >
                        {setupLoading ? <PiSpinner size={16} className="spin" /> : 'Create & assign'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`rep-toast rep-toast-${toast.type}`}>
          {toast.type === 'success' ? <PiCheckCircle size={18} /> : <PiWarning size={18} />}
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default RepDashboard;
